/**
 * Dữ liệu mẫu và máy chủ giả của các màn tri thức (module `tri-thuc`).
 *
 * Đóng vai backend khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`. Máy chủ giả **từ chối
 * y như backend** — cùng `error_code`, cùng `pointer` — để nhánh lỗi của form chạy được khi chưa có
 * backend:
 *
 * - tạo bộ mã `KB001` (đã có) nhận `409 TRI_THUC_KNOWLEDGE_BASE_CODE_EXISTS`;
 * - xoá bộ `Văn bản pháp quy` nhận `409 TRI_THUC_KNOWLEDGE_BASE_IN_USE` (chatbot đang gắn);
 * - tải `.doc` nhận `400 TRI_THUC_FILE_TYPE_NOT_SUPPORTED`;
 * - "Đồng bộ lại" tệp đang nạp nhận `409 TRI_THUC_INDEX_IN_PROGRESS`;
 * - tài liệu trùng tiêu đề trong bộ nhận `409 TRI_THUC_DOCUMENT_TITLE_EXISTS`.
 *
 * Tệp vừa tải lên ở `CHO_INDEX`, vài giây sau thành `DA_INDEX` (trừ tệp tên có chữ `loi`, thành `LOI`)
 * để soát Badge đổi trạng thái. Có đủ nhánh xấu: tên rất dài, bộ chưa có collection, tệp kẹt, tệp chỉ
 * có thông tin.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import type {
  DocumentVersion,
  Folder,
  KhoSua,
  KhoTao,
  KnowledgeBase,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  KnowledgeFile,
  KnowledgeStats,
  LoaiTep,
  NguoiDanhBa,
  NguoiDuocDung,
  PhongBan,
  QuyenNguoiTep,
  QuyenThuMuc,
  TepSua,
  Trang,
  TrangThaiTaiLieu,
  UrlTaiXuong,
} from '../modules/tri-thuc/types';

const PHONG_BAN: PhongBan[] = [
  { id: 'pb-phap-che', name: 'Phòng Pháp chế', parent_id: null },
  { id: 'pb-ke-toan', name: 'Phòng Kế toán', parent_id: null },
  { id: 'pb-kinh-doanh', name: 'Phòng Kinh doanh và Phát triển thị trường miền Trung', parent_id: null },
];
const TEN_PB = new Map(PHONG_BAN.map((p) => [p.id, p.name]));

const NGUOI: NguoiDanhBa[] = [
  { user_id: 'u-004', display_name: 'Phạm Thu Hà', department_id: 'pb-phap-che', department_name: 'Phòng Pháp chế', avatar_url: null },
  { user_id: 'u-010', display_name: 'Đỗ Hoài Nam', department_id: 'pb-phap-che', department_name: 'Phòng Pháp chế', avatar_url: null },
  { user_id: 'u-011', display_name: 'Nguyễn Văn Bình', department_id: 'pb-ke-toan', department_name: 'Phòng Kế toán', avatar_url: null },
  { user_id: 'u-012', display_name: 'Tôn Nữ Thị Phương Thảo', department_id: 'pb-ke-toan', department_name: 'Phòng Kế toán', avatar_url: null },
  { user_id: 'u-015', display_name: 'Lê Minh Khang', department_id: 'pb-kinh-doanh',
    department_name: 'Phòng Kinh doanh và Phát triển thị trường miền Trung', avatar_url: null },
];

const TOI = 'u-004';
const pb = (id: string) => ({ id, name: TEN_PB.get(id) ?? null });
const loi = (status: number, code: string, message: string, details?: unknown): ApiError => ({ code, status, message, details });
const bayGio = () => new Date().toISOString();

let tuTang = 100;
const idMoi = (tien: string) => `${tien}-${++tuTang}`;

const kho = (so: number, code: string, name: string, quanLy: string, duocDung: string[], phu: Partial<KnowledgeBase> = {}):
  KnowledgeBase => ({
  id: `kb-${so}`,
  code,
  name,
  description: null,
  managing_department: pb(quanLy),
  permitted_departments: duocDung.map(pb),
  collection_id: `col-${so}`,
  collection_error: null,
  chunk_mode: 'tu_dong',
  chunk_size: null,
  chunk_overlap: null,
  chunk_gemini_cau_truc: null,
  an_tai_lieu_ngoai_hieu_luc: false,
  file_counts: { word: 0, excel: 0, pdf: 0, image: 0, powerpoint: 0, text: 0, total: 0 },
  created_at: `2026-09-${String(10 + so).padStart(2, '0')}T02:00:00Z`,
  updated_at: `2026-10-${String(1 + so).padStart(2, '0')}T03:30:00Z`,
  created_by: TOI,
  ...phu,
});

const KHO: KnowledgeBase[] = [
  kho(1, 'KB001', 'Văn bản pháp quy', 'pb-phap-che', ['pb-phap-che', 'pb-ke-toan'],
    { description: 'Luật, nghị định, thông tư áp dụng cho công ty.', an_tai_lieu_ngoai_hieu_luc: true }),
  kho(2, 'KB002', 'Quy trình nội bộ', 'pb-ke-toan', ['pb-ke-toan'],
    { chunk_mode: 'thu_cong', chunk_size: 800, chunk_overlap: 100, chunk_gemini_cau_truc: true }),
  kho(3, 'KB_BAO_GIA_2026', 'Bảng giá và chính sách bán hàng năm 2026 cho khách hàng doanh nghiệp khu vực miền Trung',
    'pb-kinh-doanh', ['pb-kinh-doanh']),
  kho(4, 'KB_MOI', 'Hỏi đáp khách hàng', 'pb-kinh-doanh', [],
    { collection_id: null, collection_error: 'Module chưa được cấu hình kết nối tới index-service.' }),
];

/** Chatbot đang gắn với collection: chặn xoá bộ như backend. */
const CHATBOT_DANG_DUNG = new Map([['col-1', ['Trợ lý pháp chế']]]);

const THU_MUC: Folder[] = [
  { id: 'f-1', knowledge_base_id: 'kb-1', name: 'Nghị định', parent_id: null, permissions_configured: false, created_at: '2026-09-12T02:00:00Z',
    updated_at: '2026-09-12T02:00:00Z', created_by: TOI },
  { id: 'f-2', knowledge_base_id: 'kb-1', name: 'Thông tư', parent_id: null, permissions_configured: true, created_at: '2026-09-12T02:00:00Z',
    updated_at: '2026-09-12T02:00:00Z', created_by: TOI },
  { id: 'f-3', knowledge_base_id: 'kb-1', name: '2026', parent_id: 'f-2', permissions_configured: false, created_at: '2026-09-12T02:00:00Z',
    updated_at: '2026-09-12T02:00:00Z', created_by: TOI },
];
const QUYEN_THU_MUC = new Map<string, string[]>([['f-2', ['u-004', 'u-010']]]);

const tep = (so: number, kbId: string | null, file_name: string, file_type: LoaiTep, phu: Partial<KnowledgeFile> = {}): KnowledgeFile => {
  const k = KHO.find((x) => x.id === kbId);
  return {
    id: `t-${so}`,
    knowledge_base_id: kbId,
    knowledge_base_name: k?.name ?? null,
    folder_id: null,
    file_name,
    file_type,
    file_size_bytes: 120_000 + so * 37_500,
    content_type: 'application/pdf',
    has_file: true,
    uploaded_by: TOI,
    uploaded_at: `2026-10-${String(1 + (so % 20)).padStart(2, '0')}T04:00:00Z`,
    effective_from: '2026-01-01',
    effective_to: null,
    source_document_id: null,
    document_index_id: `doc-idx-${so}`,
    index_status: 'DA_INDEX',
    index_error: null,
    index_warning: null,
    index_attempts: 1,
    index_attempted_at: `2026-10-${String(1 + (so % 20)).padStart(2, '0')}T04:00:05Z`,
    index_stuck: false,
    dept_permissions_configured: false,
    user_permissions_configured: true,
    permitted_departments: [],
    created_at: `2026-10-${String(1 + (so % 20)).padStart(2, '0')}T04:00:00Z`,
    updated_at: `2026-10-${String(1 + (so % 20)).padStart(2, '0')}T04:00:00Z`,
    created_by: TOI,
    ...phu,
  };
};

const TEP: KnowledgeFile[] = [
  tep(1, 'kb-1', 'Nghị định 13-2023 bảo vệ dữ liệu cá nhân.pdf', 'PDF', { folder_id: 'f-1', effective_to: '2027-12-31' }),
  tep(2, 'kb-1', 'Thông tư 09-2026 hướng dẫn hoá đơn điện tử.pdf', 'PDF', { folder_id: 'f-3',
    index_warning: 'Đã nạp tệp nhưng bỏ qua bước dựng tiêu đề bằng AI: Tổ chức đã hết hạn mức token AI tháng này. Đoạn chia có thể kém chính xác hơn; nạp lại tệp sau khi khắc phục để chia đoạn tốt hơn.' }),
  tep(3, 'kb-1', 'Luật Doanh nghiệp 2020.docx', 'WORD', { index_status: 'LOI', document_index_id: null, index_attempts: 3,
    index_error: 'index-service quá 60000 ms không trả lời' }),
  tep(4, 'kb-1', 'Biểu mẫu báo cáo thuế quý.xlsx', 'EXCEL', { index_status: 'CHO_INDEX', document_index_id: null, index_stuck: true,
    index_attempts: 0, index_attempted_at: null }),
  tep(5, 'kb-1', 'Ghi chú họp pháp chế tháng 9', 'TEXT', { has_file: false, index_status: 'CHUA_INDEX', document_index_id: null,
    user_permissions_configured: false, effective_from: null }),
  tep(6, 'kb-2', 'Quy trình thanh toán công tác phí.pdf', 'PDF', { dept_permissions_configured: true, permitted_departments: [pb('pb-ke-toan')] }),
  tep(7, 'kb-3', 'Bảng giá dịch vụ triển khai và bảo trì hệ thống cho khách hàng doanh nghiệp khu vực miền Trung năm 2026.xlsx',
    'EXCEL'),
];
const QUYEN_NGUOI_TEP = new Map<string, string[]>(TEP.filter((t) => t.user_permissions_configured).map((t) => [t.id, ['u-004']]));

const taiLieu = (so: number, kbId: string, title: string, status: TrangThaiTaiLieu, phu: Partial<KnowledgeDocument> = {}):
  KnowledgeDocumentDetail => ({
  id: `d-${so}`,
  knowledge_base_id: kbId,
  knowledge_base_name: KHO.find((k) => k.id === kbId)?.name ?? '',
  title,
  file_type: 'PDF',
  file_size_bytes: 240_000 + so * 11_000,
  content_type: 'application/pdf',
  has_file: true,
  uploaded_by: 'u-011',
  uploaded_at: `2026-10-${String(2 + so).padStart(2, '0')}T02:00:00Z`,
  status,
  current_version: 'v1.0',
  version_count: 1,
  review_note: null,
  reviewed_by: null,
  reviewed_at: null,
  created_at: `2026-10-${String(2 + so).padStart(2, '0')}T02:00:00Z`,
  updated_at: `2026-10-${String(3 + so).padStart(2, '0')}T02:00:00Z`,
  created_by: 'u-011',
  versions: [{ id: `v-${so}-1`, document_id: `d-${so}`, version_number: 'v1.0', change_note: 'Phiên bản đầu tiên',
    content_summary: null, status, created_at: `2026-10-${String(2 + so).padStart(2, '0')}T02:00:00Z`, created_by: 'u-011' }],
  ...phu,
});

const TAI_LIEU: KnowledgeDocumentDetail[] = [
  taiLieu(1, 'kb-2', 'Quy chế chi tiêu nội bộ 2026', 'REVIEW'),
  taiLieu(2, 'kb-2', 'Hướng dẫn đối soát công nợ', 'DRAFT', { review_note: 'Bổ sung phụ lục mẫu biên bản đối soát.',
    reviewed_by: 'u-004', reviewed_at: '2026-10-08T02:00:00Z' }),
  taiLieu(3, 'kb-1', 'Quy định bảo vệ dữ liệu cá nhân trong công ty', 'APPROVED'),
  taiLieu(4, 'kb-1', 'Chính sách lưu trữ hồ sơ cũ', 'ARCHIVED'),
];

/** Phân trang kiểu con trỏ của backend, ở đây con trỏ là vị trí. */
function trang<T>(ds: T[], limit: number, cursor: string | undefined): Trang<T> {
  const tu = cursor ? Number(cursor) : 0;
  const items = ds.slice(tu, tu + limit);
  return { items, next_cursor: tu + limit < ds.length ? String(tu + limit) : null };
}

function demTep(k: KnowledgeBase): KnowledgeBase {
  const ds = TEP.filter((t) => t.knowledge_base_id === k.id);
  const n = (l: LoaiTep) => ds.filter((t) => t.file_type === l).length;
  return { ...k, file_counts: { word: n('WORD'), excel: n('EXCEL'), pdf: n('PDF'), image: n('IMAGE'), powerpoint: n('POWERPOINT'),
    text: n('TEXT'), total: ds.length } };
}

function timKho(id: string): KnowledgeBase {
  const k = KHO.find((x) => x.id === id);
  if (!k) throw loi(404, 'NOT_FOUND', 'Không tìm thấy bộ tri thức.');
  return k;
}

function timTep(id: string): KnowledgeFile {
  const t = TEP.find((x) => x.id === id);
  if (!t) throw loi(404, 'NOT_FOUND', 'Không tìm thấy tệp tri thức.');
  return t;
}

function timTaiLieu(id: string): KnowledgeDocumentDetail {
  const d = TAI_LIEU.find((x) => x.id === id);
  if (!d) throw loi(404, 'NOT_FOUND', 'Không tìm thấy tài liệu.');
  return d;
}

function moTaNguoi(ids: string[]): NguoiDuocDung[] {
  return ids.map((id) => {
    const u = NGUOI.find((x) => x.user_id === id);
    return { user_id: id, display_name: u?.display_name ?? null, department_id: u?.department_id ?? null,
      department_name: u?.department_name ?? null };
  });
}

const LOAI_THEO_DUOI: Record<string, LoaiTep> = {
  '.pdf': 'PDF', '.docx': 'WORD', '.xlsx': 'EXCEL', '.xls': 'EXCEL', '.pptx': 'POWERPOINT', '.ppt': 'POWERPOINT', '.txt': 'TEXT',
  '.jpg': 'IMAGE', '.jpeg': 'IMAGE', '.png': 'IMAGE', '.gif': 'IMAGE', '.webp': 'IMAGE',
};

function nhanDang(ten: string): LoaiTep {
  const duoi = /\.[^.]+$/.exec(ten)?.[0]?.toLowerCase() ?? '';
  if (duoi === '.doc') {
    throw loi(400, 'TRI_THUC_FILE_TYPE_NOT_SUPPORTED', 'Định dạng .doc (Word 97-2003) không còn được hỗ trợ. Mở tệp bằng Word rồi chọn '
      + '"Lưu dưới dạng" → .docx, sau đó tải lên lại.', [{ pointer: '#/file', detail: 'Đuôi .doc không được nhận.' }]);
  }
  const l = LOAI_THEO_DUOI[duoi];
  if (!l) throw loi(400, 'TRI_THUC_FILE_TYPE_NOT_SUPPORTED', 'Đuôi tệp không được nhận.', [{ pointer: '#/file', detail: 'Đuôi không được nhận.' }]);
  return l;
}

/** Tệp đang chờ thì vài giây sau xong, như worker thật; tên có chữ `loi` thì lỗi. */
function napGia(t: KnowledgeFile): void {
  t.index_status = 'CHO_INDEX';
  t.index_error = null;
  t.index_warning = null;
  t.index_stuck = false;
  t.index_attempts = 0;
  window.setTimeout(() => {
    if (!TEP.includes(t)) return;
    const hong = /loi/i.test(t.file_name);
    t.index_attempts = hong ? 3 : 1;
    t.index_attempted_at = bayGio();
    t.index_status = hong ? 'LOI' : 'DA_INDEX';
    t.index_error = hong ? 'index-service từ chối tải tệp lên (HTTP 500)' : null;
    t.document_index_id = hong ? null : idMoi('doc-idx');
  }, 4000);
}

const URL_GIA = (ten: string): UrlTaiXuong => ({
  url: `data:text/plain;charset=utf-8,${encodeURIComponent(`Tệp mẫu: ${ten}`)}`,
  expires_at: new Date(Date.now() + 300_000).toISOString(),
});

const CHUYEN: Record<string, Partial<Record<TrangThaiTaiLieu, TrangThaiTaiLieu>>> = {
  'submit-review': { DRAFT: 'REVIEW' },
  approve: { REVIEW: 'APPROVED' },
  'return-draft': { REVIEW: 'DRAFT' },
  'request-revision': { REVIEW: 'DRAFT' },
  archive: { APPROVED: 'ARCHIVED' },
  rollback: { REVIEW: 'DRAFT', APPROVED: 'REVIEW', ARCHIVED: 'APPROVED' },
};

function kiemTieuDe(kbId: string, title: string, boQua?: string): void {
  if (TAI_LIEU.some((d) => d.id !== boQua && d.knowledge_base_id === kbId && d.status !== 'ARCHIVED'
    && d.title.toLowerCase() === title.toLowerCase())) {
    throw loi(409, 'TRI_THUC_DOCUMENT_TITLE_EXISTS', `Tài liệu "${title}" đã có trong bộ tri thức này.`,
      [{ pointer: '#/title', detail: 'Tiêu đề đã được dùng.' }]);
  }
}

export const mayChuTriThuc = {
  danhSachKho({ limit, cursor, search, department_id }: { limit: number; cursor?: string; search?: string; department_id?: string }):
    Trang<KnowledgeBase> {
    const s = search?.toLowerCase();
    const ds = KHO.filter((k) => (!s || k.name.toLowerCase().includes(s) || k.code.toLowerCase().includes(s))
      && (!department_id || k.managing_department.id === department_id || k.permitted_departments.some((p) => p.id === department_id)))
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return trang(ds.map(demTep), limit, cursor);
  },

  thongKe(): KnowledgeStats {
    const loai: KnowledgeStats['files_by_type'] = {};
    for (const t of TEP) loai[t.file_type.toLowerCase() as keyof typeof loai] = (loai[t.file_type.toLowerCase() as keyof typeof loai] ?? 0) + 1;
    return {
      total_knowledge_bases: KHO.length,
      total_files: TEP.length,
      pdf_files_count: TEP.filter((t) => t.file_type === 'PDF').length,
      departments_using_count: new Set(KHO.flatMap((k) => [k.managing_department.id, ...k.permitted_departments.map((p) => p.id)])).size,
      last_updated_at: KHO.map((k) => k.updated_at).sort().at(-1) ?? null,
      files_by_type: loai,
      files_by_knowledge_base: KHO.map((k) => ({ knowledge_base_id: k.id, name: k.name, file_count: demTep(k).file_counts.total }))
        .filter((x) => x.file_count > 0).sort((a, b) => b.file_count - a.file_count),
    };
  },

  chiTietKho: (id: string): KnowledgeBase => demTep(timKho(id)),

  taoKho(du: KhoTao): KnowledgeBase {
    if (KHO.some((k) => k.code === du.code)) {
      throw loi(409, 'TRI_THUC_KNOWLEDGE_BASE_CODE_EXISTS', `Mã bộ tri thức "${du.code}" đã có, vui lòng chọn mã khác.`,
        [{ pointer: '#/code', detail: 'Mã này đã được dùng.' }]);
    }
    const k = kho(++tuTang, du.code, du.name, du.managing_department_id, du.permitted_department_ids ?? [], {
      id: idMoi('kb'), description: du.description ?? null, chunk_mode: du.chunk_mode ?? 'tu_dong', chunk_size: du.chunk_size ?? null,
      chunk_overlap: du.chunk_overlap ?? null, chunk_gemini_cau_truc: du.chunk_gemini_cau_truc ?? null,
      an_tai_lieu_ngoai_hieu_luc: du.an_tai_lieu_ngoai_hieu_luc ?? false, created_at: bayGio(), updated_at: bayGio(),
    });
    KHO.unshift(k);
    return demTep(k);
  },

  suaKho(id: string, du: KhoSua): KnowledgeBase {
    const k = timKho(id);
    if (du.name !== undefined) k.name = du.name;
    if (du.description !== undefined) k.description = du.description;
    if (du.managing_department_id) k.managing_department = pb(du.managing_department_id);
    if (du.permitted_department_ids) k.permitted_departments = du.permitted_department_ids.map(pb);
    if (du.an_tai_lieu_ngoai_hieu_luc !== undefined) k.an_tai_lieu_ngoai_hieu_luc = du.an_tai_lieu_ngoai_hieu_luc;
    k.updated_at = bayGio();
    return demTep(k);
  },

  xoaKho(id: string): void {
    const k = timKho(id);
    const bot = k.collection_id ? CHATBOT_DANG_DUNG.get(k.collection_id) : undefined;
    if (bot?.length) {
      throw loi(409, 'TRI_THUC_KNOWLEDGE_BASE_IN_USE', `Không xoá được: bộ tri thức đang được chatbot ${bot.join(', ')} sử dụng. `
        + 'Hãy gỡ liên kết khỏi các chatbot này trước khi xoá.');
    }
    KHO.splice(KHO.indexOf(k), 1);
    for (let i = TEP.length - 1; i >= 0; i--) if (TEP[i]!.knowledge_base_id === id) TEP.splice(i, 1);
  },

  danhSachThuMuc: (kbId: string): { items: Folder[] } => {
    timKho(kbId);
    return { items: THU_MUC.filter((f) => f.knowledge_base_id === kbId) };
  },

  taoThuMuc(kbId: string, du: { name: string; parent_id: string | null }): Folder {
    timKho(kbId);
    const f: Folder = { id: idMoi('f'), knowledge_base_id: kbId, name: du.name, parent_id: du.parent_id, permissions_configured: false,
      created_at: bayGio(), updated_at: bayGio(), created_by: TOI };
    THU_MUC.push(f);
    return f;
  },

  doiTenThuMuc(fid: string, name: string): Folder {
    const f = THU_MUC.find((x) => x.id === fid);
    if (!f) throw loi(404, 'NOT_FOUND', 'Không tìm thấy thư mục.');
    f.name = name;
    return f;
  },

  xoaThuMuc(fid: string): void {
    const cay = new Set([fid]);
    for (let them = true; them;) {
      them = false;
      for (const f of THU_MUC) if (f.parent_id && cay.has(f.parent_id) && !cay.has(f.id)) {
        cay.add(f.id);
        them = true;
      }
    }
    for (let i = THU_MUC.length - 1; i >= 0; i--) if (cay.has(THU_MUC[i]!.id)) THU_MUC.splice(i, 1);
    for (const t of TEP) if (t.folder_id && cay.has(t.folder_id)) t.folder_id = null;
  },

  quyenThuMuc: (kbId: string): { items: QuyenThuMuc[] } => ({
    items: THU_MUC.filter((f) => f.knowledge_base_id === kbId).map((f) => ({ folder_id: f.id, configured: f.permissions_configured,
      permitted_users: moTaNguoi(QUYEN_THU_MUC.get(f.id) ?? []) })),
  }),

  datQuyenThuMuc(fid: string, ids: string[]): QuyenThuMuc {
    const f = THU_MUC.find((x) => x.id === fid);
    if (!f) throw loi(404, 'NOT_FOUND', 'Không tìm thấy thư mục.');
    f.permissions_configured = true;
    QUYEN_THU_MUC.set(fid, ids);
    return { folder_id: fid, configured: true, permitted_users: moTaNguoi(ids) };
  },

  danhSachTep({ knowledge_base_id, folder_id, search, file_type, limit, cursor }: { knowledge_base_id?: string; folder_id?: string;
    search?: string; file_type?: LoaiTep; limit: number; cursor?: string }): Trang<KnowledgeFile> {
    const s = search?.toLowerCase();
    const ds = TEP.filter((t) => (!knowledge_base_id || t.knowledge_base_id === knowledge_base_id)
      && (!folder_id || (folder_id === 'root' ? t.folder_id === null : t.folder_id === folder_id))
      && (!file_type || t.file_type === file_type)
      && (!s || t.file_name.toLowerCase().includes(s)))
      .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    return trang(ds.map((t) => ({ ...t })), limit, cursor);
  },

  taiTep(du: { knowledge_base_id: string; effective_from: string; effective_to?: string; tenTep: string; kichThuoc: number }): KnowledgeFile {
    const k = timKho(du.knowledge_base_id);
    const t = tep(++tuTang, k.id, du.tenTep, nhanDang(du.tenTep), { id: idMoi('t'), effective_from: du.effective_from,
      effective_to: du.effective_to ?? null, file_size_bytes: du.kichThuoc, uploaded_at: bayGio(), created_at: bayGio(),
      updated_at: bayGio(), document_index_id: null, user_permissions_configured: false });
    TEP.unshift(t);
    k.updated_at = bayGio();
    napGia(t);
    return { ...t };
  },

  suaTep(id: string, du: TepSua): KnowledgeFile {
    const t = timTep(id);
    const tu = du.effective_from !== undefined ? du.effective_from : t.effective_from;
    const den = du.effective_to !== undefined ? du.effective_to : t.effective_to;
    if (tu && den && den <= tu) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thông tin tệp tri thức chưa đúng, vui lòng sửa các trường được đánh dấu.',
        [{ pointer: '#/effective_to', detail: 'Ngày hết hiệu lực phải sau ngày bắt đầu hiệu lực.' }]);
    }
    if (du.file_name !== undefined) t.file_name = du.file_name;
    t.effective_from = tu;
    t.effective_to = den;
    if (du.folder_id !== undefined) {
      t.folder_id = du.folder_id;
      if (du.folder_id) QUYEN_NGUOI_TEP.set(id, QUYEN_THU_MUC.get(du.folder_id) ?? []);
    }
    t.updated_at = bayGio();
    return { ...t };
  },

  chuyenBo(id: string, kbId: string | null): KnowledgeFile {
    const t = timTep(id);
    if (kbId) timKho(kbId);
    t.knowledge_base_id = kbId;
    t.knowledge_base_name = kbId ? KHO.find((k) => k.id === kbId)?.name ?? null : null;
    t.folder_id = null;
    if (kbId && t.has_file) napGia(t);
    else {
      t.index_status = 'CHUA_INDEX';
      t.document_index_id = null;
    }
    return { ...t };
  },

  dongBoLai(id: string): KnowledgeFile {
    const t = timTep(id);
    if (!t.has_file) throw loi(409, 'TRI_THUC_FILE_NOT_INDEXABLE', 'Tệp này chỉ có thông tin, chưa kèm tệp nên không nạp được vào tìm kiếm.');
    if (!t.knowledge_base_id) throw loi(409, 'TRI_THUC_FILE_NOT_INDEXABLE', 'Tệp chưa thuộc bộ tri thức nào. Hãy chuyển tệp vào một bộ trước.');
    if ((t.index_status === 'CHO_INDEX' || t.index_status === 'DANG_INDEX') && !t.index_stuck) {
      throw loi(409, 'TRI_THUC_INDEX_IN_PROGRESS', 'Tệp đang được nạp vào tìm kiếm, vui lòng chờ xong rồi thử lại.');
    }
    napGia(t);
    return { ...t };
  },

  xoaTep(id: string): void {
    const t = timTep(id);
    TEP.splice(TEP.indexOf(t), 1);
  },

  taiXuongTep(id: string): UrlTaiXuong {
    const t = timTep(id);
    if (!t.has_file) throw loi(404, 'NOT_FOUND', 'Tệp này chỉ có thông tin, chưa kèm tệp để tải xuống.');
    return URL_GIA(t.file_name);
  },

  datPhongBanTep(id: string, ids: string[]): KnowledgeFile {
    const t = timTep(id);
    t.dept_permissions_configured = true;
    t.permitted_departments = ids.map(pb);
    return { ...t };
  },

  datNguoiTep(id: string, ids: string[]): QuyenNguoiTep {
    const t = timTep(id);
    t.user_permissions_configured = true;
    QUYEN_NGUOI_TEP.set(id, ids);
    return { file_id: id, configured: true, permitted_users: moTaNguoi(ids) };
  },

  quyenNguoiTep: (kbId: string): { items: QuyenNguoiTep[] } => ({
    items: TEP.filter((t) => t.knowledge_base_id === kbId).map((t) => ({ file_id: t.id, configured: t.user_permissions_configured,
      permitted_users: moTaNguoi(QUYEN_NGUOI_TEP.get(t.id) ?? []) })),
  }),

  danhSachTaiLieu({ knowledge_base_id, status, search, limit, cursor }: { knowledge_base_id?: string; status?: TrangThaiTaiLieu;
    search?: string; limit: number; cursor?: string }): Trang<KnowledgeDocument> {
    const s = search?.toLowerCase();
    const ds = TAI_LIEU.filter((d) => (!knowledge_base_id || d.knowledge_base_id === knowledge_base_id) && (!status || d.status === status)
      && (!s || d.title.toLowerCase().includes(s))).sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return trang(ds.map(({ versions: _v, ...d }) => d), limit, cursor);
  },

  chiTietTaiLieu: (id: string): KnowledgeDocumentDetail => structuredClone(timTaiLieu(id)),

  taoTaiLieu(du: { knowledge_base_id: string; title: string; note?: string; content_summary?: string; tenTep: string; kichThuoc: number }):
    KnowledgeDocumentDetail {
    const k = timKho(du.knowledge_base_id);
    kiemTieuDe(k.id, du.title);
    const so = ++tuTang;
    const d = taiLieu(so, k.id, du.title, 'DRAFT', { id: idMoi('d'), file_type: nhanDang(du.tenTep), file_size_bytes: du.kichThuoc,
      uploaded_by: TOI, created_by: TOI, uploaded_at: bayGio(), created_at: bayGio(), updated_at: bayGio() });
    d.versions = [{ ...d.versions[0]!, document_id: d.id, change_note: du.note || 'Phiên bản đầu tiên', content_summary: du.content_summary ?? null,
      created_by: TOI, created_at: bayGio() }];
    TAI_LIEU.unshift(d);
    return structuredClone(d);
  },

  chuyenTaiLieu(id: string, thaoTac: string, note?: string): KnowledgeDocumentDetail {
    const d = timTaiLieu(id);
    const sang = CHUYEN[thaoTac]?.[d.status];
    if (!sang) throw loi(409, 'TRI_THUC_DOCUMENT_STATUS_CONFLICT', 'Thao tác này không hợp lệ với trạng thái hiện tại của tài liệu.');
    if (d.status === 'ARCHIVED') kiemTieuDe(d.knowledge_base_id, d.title, d.id);
    d.status = sang;
    if (thaoTac === 'approve' || thaoTac === 'return-draft' || thaoTac === 'request-revision') {
      d.review_note = note ?? null;
      d.reviewed_by = TOI;
      d.reviewed_at = bayGio();
    }
    const v = d.versions.find((x) => x.version_number === d.current_version);
    if (v) v.status = sang;
    d.updated_at = bayGio();
    if (thaoTac === 'approve') {
      const t = TEP.find((x) => x.source_document_id === d.id) ?? tep(++tuTang, d.knowledge_base_id, d.title, d.file_type,
        { id: idMoi('t'), source_document_id: d.id, document_index_id: null, effective_from: null });
      if (!TEP.includes(t)) TEP.unshift(t);
      napGia(t);
    }
    return structuredClone(d);
  },

  phienBanMoi(id: string, du: { change_note: string; content_summary?: string | null }): KnowledgeDocumentDetail {
    const d = timTaiLieu(id);
    const m = /^v(\d+)\.(\d+)$/.exec(d.current_version);
    const ver = m ? `v${m[1]}.${Number(m[2]) + 1}` : `${d.current_version}.1`;
    const v: DocumentVersion = { id: idMoi('v'), document_id: d.id, version_number: ver, change_note: du.change_note,
      content_summary: du.content_summary ?? null, status: 'DRAFT', created_at: bayGio(), created_by: TOI };
    d.versions.push(v);
    d.current_version = ver;
    d.version_count += 1;
    d.status = 'DRAFT';
    d.updated_at = bayGio();
    return structuredClone(d);
  },

  taiXuongTaiLieu(id: string): UrlTaiXuong {
    const d = timTaiLieu(id);
    return URL_GIA(d.title);
  },

  phongBan: (): { items: PhongBan[] } => ({ items: PHONG_BAN }),

  nguoiTheoPhongBan: (deptId: string): Trang<NguoiDanhBa> => ({ items: NGUOI.filter((u) => u.department_id === deptId), next_cursor: null }),
};
